import { useSessionTokenGetter } from '@develio/auth/client';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

type OrgSummary = {
  id: string;
  name: string;
  slug: string;
  createdAt: string;
  memberCount: number;
  analysisCount: number;
};

type PipelineSummary = {
  pipeline: string;
  status: string;
  releaseLabel: string | null;
  recordsUpserted: number | null;
  lastRunAt: string | null;
  completedAt: string | null;
  errorMessage: string | null;
  sourceUrl: string | null;
};

const PIPELINE_LABELS: Record<string, string> = {
  oes: 'BLS OES — Occupational Wages',
  qcew: 'BLS QCEW — Industry Employment',
  laus: 'BLS LAUS — Labor Force',
  lehd: 'Census LEHD — Commuting Flows',
  acs: 'Census ACS — Educational Attainment',
  bea_gdp: 'BEA GDP by Industry',
  bea_fa: 'BEA Fixed Assets',
  onet: 'O*NET — Occupation Skills & Attributes',
  cbp: 'Census CBP — Establishment Counts',
  federal_designations: 'Federal Designations — OZ / NMTC / PPCo / ERS',
  bps: 'Census BPS — Building Permits',
};

const PIPELINE_CADENCE: Record<string, string> = {
  oes: 'Annual (June)',
  qcew: 'Quarterly',
  laus: 'Monthly',
  lehd: 'Annual (Sept)',
  acs: 'Annual (Dec)',
  bea_gdp: 'Annual (July)',
  bea_fa: 'Annual (July)',
  onet: 'Quarterly',
  cbp: 'Annual (Aug)',
  federal_designations: 'Annual (Jan)',
  bps: 'Annual (Mar)',
};

function PipelineStatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    success: 'bg-green-50 text-green-700',
    running: 'bg-blue-50 text-blue-700',
    failed: 'bg-red-50 text-red-700',
    never_run: 'bg-slate-100 text-slate-500',
  };
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${map[status] ?? 'bg-slate-100 text-slate-500'}`}
    >
      {status.replace('_', ' ')}
    </span>
  );
}

/**
 * Admin panel — visible only to users in ADMIN_USER_IDS env var.
 *
 * Lists all organizations and lets an admin navigate into an org's analyses
 * or impersonate the org to view its data in context.
 *
 * Route: /admin (wired outside ProtectedShell but inside Clerk context)
 */
export function AdminRoute() {
  const getToken = useSessionTokenGetter();
  const [orgs, setOrgs] = useState<OrgSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [impersonating, setImpersonating] = useState<string | null>(null);
  const [pipelines, setPipelines] = useState<PipelineSummary[]>([]);
  const [triggering, setTriggering] = useState<string | null>(null);
  const [triggerOrgId, setTriggerOrgId] = useState<Record<string, string>>({});

  const fetchOrgs = useCallback(async () => {
    const token = await getToken();
    const res = await fetch(`${API}/api/v1/admin/orgs`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const j = await res.json().catch(() => ({}));
      throw new Error((j as { error?: string }).error ?? `${res.status}`);
    }
    return res.json() as Promise<{ orgs: OrgSummary[] }>;
  }, [getToken]);

  const fetchDataSources = useCallback(async () => {
    const token = await getToken();
    const res = await fetch(`${API}/api/v1/admin/data-sources`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return;
    const json = (await res.json()) as { pipelines: PipelineSummary[] };
    setPipelines(json.pipelines);
  }, [getToken]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchOrgs();
        if (!cancelled) setOrgs(data.orgs);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
      await fetchDataSources().catch(() => null);
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchOrgs, fetchDataSources]);

  const ORG_SCOPED = ['oes', 'qcew', 'laus', 'lehd', 'acs'];

  async function triggerPipeline(pipeline: string) {
    const orgId = triggerOrgId[pipeline];
    if (ORG_SCOPED.includes(pipeline) && !orgId) {
      setError(`Select an org for "${pipeline}" before triggering.`);
      return;
    }
    setTriggering(pipeline);
    setError(null);
    try {
      const token = await getToken();
      const res = await fetch(`${API}/api/v1/admin/data-sources/${pipeline}/trigger`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(orgId ? { orgId } : {}),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error((j as { error?: string }).error ?? `${res.status}`);
      }
      await fetchDataSources();
    } catch (e) {
      setError(`Trigger failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setTriggering(null);
    }
  }

  async function impersonate(orgId: string, orgName: string) {
    setImpersonating(orgId);
    try {
      const token = await getToken();
      const res = await fetch(`${API}/api/v1/admin/impersonate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ orgId }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      // Open the org's analyses in a new tab via the admin endpoint
      window.open(`/admin/orgs/${orgId}`, '_blank', 'noreferrer');
    } catch (e) {
      setError(`Failed to impersonate ${orgName}: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setImpersonating(null);
    }
  }

  return (
    <div className="mx-auto max-w-5xl px-6 py-10">
      <div className="mb-8 border-b border-slate-200 pb-6">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">
          System Administration
        </div>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight">Organizations</h1>
        <p className="mt-1 text-sm text-slate-500">
          All tenants. Impersonation is audit-logged in the target org's audit trail.
        </p>
        <div className="mt-3 flex gap-4 text-xs">
          <Link to="/admin/features" className="text-brand-teal-600 hover:underline">
            Feature flags →
          </Link>
          <Link to="/admin/rims" className="text-brand-teal-600 hover:underline">
            National RIMS II →
          </Link>
          <Link to="/admin/states" className="text-brand-teal-600 hover:underline">
            State Records →
          </Link>
        </div>
      </div>

      {error && (
        <div className="mb-6 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-600">
          {error}
        </div>
      )}

      {loading && (
        <div className="py-12 text-center text-sm text-slate-500">Loading organizations…</div>
      )}

      {!loading && orgs.length === 0 && !error && (
        <div className="py-12 text-center text-sm text-slate-500">No organizations found.</div>
      )}

      {!loading && orgs.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-slate-500">Organization</th>
                <th className="px-4 py-3 text-left font-medium text-slate-500">Slug</th>
                <th className="px-4 py-3 text-right font-medium text-slate-500">Members</th>
                <th className="px-4 py-3 text-right font-medium text-slate-500">Analyses</th>
                <th className="px-4 py-3 text-left font-medium text-slate-500">Created</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {orgs.map((org) => (
                <tr key={org.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-900">{org.name}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-400">{org.slug}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-600">
                    {org.memberCount}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-600">
                    {org.analysisCount}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-400">
                    {new Date(org.createdAt).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <Link
                        to={`/admin/orgs/${org.id}`}
                        className="text-xs font-medium text-slate-500 hover:text-slate-900"
                      >
                        View
                      </Link>
                      <button
                        type="button"
                        onClick={() => void impersonate(org.id, org.name)}
                        disabled={impersonating === org.id}
                        className="text-xs font-medium text-indigo-600 hover:text-indigo-900 disabled:opacity-50"
                      >
                        {impersonating === org.id ? 'Opening…' : 'Impersonate'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Data Spine Freshness */}
      <div className="mt-12 border-t border-slate-200 pt-8">
        <h2 className="text-lg font-semibold tracking-tight">Data Sources</h2>
        <p className="mt-1 text-sm text-slate-500">
          Phase 0 ingestion pipeline status. Pipelines run on schedule; use Trigger to run
          on-demand.
        </p>
        <div className="mt-4 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-slate-500">Pipeline</th>
                <th className="px-4 py-3 text-left font-medium text-slate-500">Cadence</th>
                <th className="px-4 py-3 text-left font-medium text-slate-500">Status</th>
                <th className="px-4 py-3 text-left font-medium text-slate-500">Last release</th>
                <th className="px-4 py-3 text-right font-medium text-slate-500">Records</th>
                <th className="px-4 py-3 text-left font-medium text-slate-500">Last run</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {pipelines.map((p) => (
                <tr key={p.pipeline} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-900">
                    {PIPELINE_LABELS[p.pipeline] ?? p.pipeline}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-400">
                    {PIPELINE_CADENCE[p.pipeline] ?? '—'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-0.5">
                      <PipelineStatusBadge status={p.status} />
                      {p.status === 'failed' && p.errorMessage && (
                        <span
                          className="max-w-[200px] truncate text-[10px] text-red-500"
                          title={p.errorMessage}
                        >
                          {p.errorMessage}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">{p.releaseLabel ?? '—'}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-xs text-slate-500">
                    {p.recordsUpserted != null ? p.recordsUpserted.toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-400">
                    {p.lastRunAt
                      ? new Date(p.lastRunAt).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })
                      : 'Never'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {ORG_SCOPED.includes(p.pipeline) && (
                        <select
                          value={triggerOrgId[p.pipeline] ?? ''}
                          onChange={(e) =>
                            setTriggerOrgId((prev) => ({ ...prev, [p.pipeline]: e.target.value }))
                          }
                          className="rounded border border-slate-200 px-1.5 py-0.5 text-xs text-slate-600"
                        >
                          <option value="">— org —</option>
                          {orgs.map((o) => (
                            <option key={o.id} value={o.id}>
                              {o.name}
                            </option>
                          ))}
                        </select>
                      )}
                      <button
                        type="button"
                        onClick={() => void triggerPipeline(p.pipeline)}
                        disabled={triggering === p.pipeline}
                        className="text-xs font-medium text-indigo-600 hover:text-indigo-900 disabled:opacity-50"
                      >
                        {triggering === p.pipeline ? 'Running…' : 'Trigger'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
